<script>
import {defineComponent} from "vue";
import {useRoute, useRouter} from "vue-router";
import BaseDataTable from "@/javascript/views/components/charts/BaseDataTable.vue";
import Paginator from "@/javascript/views/components/Pagination.vue";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import Icon from "../Icon.vue";
import {useOrganizationStore} from "../../../stores/organizations";
import {useUserStore} from "../../../stores/user";

export default defineComponent({
  name: 'BaseIndex',
  components: {Icon, BaseDataTable, Paginator},
  props: {
    /**
     * PROP: modelStore
     * Store passed that contains the model data.
     *
     * *The following properties are required in the state of the store for the component to work properly.
     * @property {Boolean} loading - Whether the data is loading.
     * @property {String} modelName - Model getting displayed for titles and such.
     * @property {Object} meta - The pagination meta information.
     *
     * *The following actions are required in the store to work properly.
     * @function nextPage - Used when next page pagination button is clicked.
     * @function prevPage - Used when previous page pagination button is clicked.
     * @function moveToPage - Used when specific page number pagination button is clicked.
     *
     * *The following getters are required in the store to work properly.
     * @function getIndexTableData
     *    - Returns an object representing the formatted table data.
     *    - Object returned should contain a 'labels' property and a 'rows' property.
     *    - See BaseTable.vue for format of 'labels' and 'rows' properties.
     *    - **Each object in the rows property MUST contain an 'id' property with the record id.**
     **/
    modelStore: {
      type: Object,
      required: true,
    },

    disableClick: {
      type: Boolean,
      default: false
    },

    hideSearch: {
      type: Boolean,
      default: false
    }
  },
  emits: [],
  setup() {
    const router = useRouter();
    const route = useRoute();
    const analyticEventStore = useAnalyticEventStore();
    const organizationStore = useOrganizationStore();

    return {
      router,
      route,
      analyticEventStore,
      organizationStore
    };
  },
  data() {
    return {
      showSearchbar: false,
      searchQuery: ""
    };
  },
  computed: {
    loading() {
      return this.modelStore.loading;
    },
    tableData() {
      this.modelStore.organization = this.organizationStore.organization;// Set this temporarily to be able to set certain dynamic table rows
      return this.modelStore.getIndexTableData;
    },
    meta() {
      return this.modelStore.meta;
    },
    tableColumns() {
      if (this.tableData.labels && this.tableData.labels.length > 0) {
        return this.tableData.labels.length + 1;
      } else {
        return 5;
      }
    }
  },
  watch: {
    'modelStore.filter.search.term'(term) {
      this.searchQuery = term || "";
      this.showSearchbar = !!this.searchQuery;
    }
  },
  mounted() {
    this.searchQuery = this.modelStore?.filter?.search?.term;
    this.showSearchbar = !!this.searchQuery;
  },
  methods: {
    navigateToShow(slug) {
      const userStore = useUserStore();
      if (!this.disableClick) {
        if (userStore.userScopedToSubdomain) {
          this.router.push({
            name: "client-dashboard-scoped", params: {
              ...this.route.params,
              id: slug
            }
          });
        } else {
          this.router.push({
            name: "client-dashboard", params: {
              ...this.route.params,
              id: slug
            }
          });
        }
      }
    },

    // This is either the magnifying glass or X depending on if the search is showing
    searchIconClicked() {
      if (!this.showSearchbar) {
        this.showSearchbar = true; // Show the search bar if hidden
      } else {
        // If the search bar is showing, clear the search term to reset
        if (this.searchQuery) {
          this.searchQuery = "";
          this.search();
        }
        this.showSearchbar = false;
      }
    },

    // Sort function, gets called when the sorting icons get clicked
    // Uses the index table data to determine the key to sort by
    // - Key is the generic one that is used if the key belongs to the model itself ie: name on fund
    // - ransackKey is used for relational queries like 'user_email' for a donor, maps directly to ransack queries
    sort(label) {
      if (label.ransackKey) {
        this.modelStore.sort(label.ransackKey);
      } else if (label.key) {
        this.modelStore.sort(label.key);
      }
    },

    search() {
      this.modelStore.search(this.searchQuery);
    },

    filter(label, option) {
      console.log('filter from index', label, option);
      this.modelStore.filterRecords(label.key, option);
    }
  }
});
</script>

<template>
  <div class="dashboard-model-index">
    <header class="breakpoint-container">
      <!--      <h1 class="title-bold">-->
      <!--        {{ modelStore.modelName }}s-->
      <!--      </h1>-->
      <div class="button-slot-container">
        <!--
          Slot for the buttons in the page header, next to the title.
        -->
        <slot name="buttons" />
      </div>
    </header>
    <main class="table-container breakpoint-container">
      <div class="overflow-container">
        <div
          v-if="!hideSearch"
          class="search-bar-wrapper w-full flex flex-row  justify-end"
        >
          <div class="relative h-full">
            <icon
              :icon="showSearchbar ? 'x' : 'search'"
              size="20px"
              class="search-icon"
              @click="searchIconClicked"
            />
            <transition name="fade-slide">
              <div
                v-if="showSearchbar"
                class="input-wrapper"
              >
                <input
                  v-model="searchQuery"
                  type="text"
                  class="search-input"
                  placeholder="Search..."
                  @keyup.enter="search"
                >
              </div>
            </transition>
          </div>
        </div>

        <base-data-table
          v-bind="tableData"
          :clickable-rows="!disableClick"
          :loading="loading"
          :filters="modelStore.filter?.filters || {}"
          no-data-message="No Records Found"
          :loading-cells="tableColumns"
          :loading-rows="modelStore.meta?.limit || 10"
          :sorting="modelStore.filter?.sort"
          @row-click="(slug) => navigateToShow(slug)"
          @sort="sort"
          @filter="filter"
        >
          <!--
            Slot for a single button to go in the table header.
          -->
          <slot name="tableHeaderButton" />
        </base-data-table>
      </div>
      <paginator
        v-if="meta"
        class="pagination"
        :page="meta.page"
        :total-pages="meta.pages"
        :records-showing="tableData?.rows?.length"
        :records-per-page="meta.limit"
        :total-records="meta.count"
        @next="modelStore.nextPage"
        @prev="modelStore.prevPage"
        @move-to-page="modelStore.moveToPage"
      />
    </main>
    <!--
      Slot for any modals required for the model's index page.
    -->
    <slot name="modals" />
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.dashboard-model-index {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  //gap: $space-lg;

  .breakpoint-container {
    width: 100%;
    //max-width: $breakpoint-max-2xl;
  }

  .table-container {
    background-color: white;
    border-radius: 10px;
    //padding: 24px;
    padding: 12px 0;
  }

  header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .button-slot-container {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: $space-sm;
    }
  }

  main {
    .overflow-container {
      overflow-x: auto;
    }

    .pagination {
      margin: $space-lg 0 $space-sm;
    }
  }
}


.search-bar-wrapper {
  height: 35px;
}

.input-wrapper {
  position: relative;
  display: inline-block;
  margin-right: 12px;
}

.search-input {
  padding: 6px 32px 6px 12px; /* space for icon */
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 250px;
}

.search-icon {
  @apply cursor-pointer absolute;
  top: 6px;
  right: 20px;
  z-index: 1;

}

/* Animation */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(10px);
}
</style>
